import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, Typography, Button, Tag, Space, Tabs,
  Form, Input, Select, message, Alert, Spin, Empty, Result,
  Popconfirm, Table,
} from 'antd';
import {
  VideoCameraOutlined, FacebookOutlined, SendOutlined,
  SoundOutlined, CheckCircleOutlined, PlayCircleOutlined,
  LoadingOutlined, DeleteOutlined, ReloadOutlined,
  CloudOutlined,
} from '@ant-design/icons';
import { videoAPI, facebookAPI } from '../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

export default function Wav2LipPage() {
  const [sourceVideos, setSourceVideos] = useState([]);
  const [voices, setVoices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fbConfigured, setFbConfigured] = useState(false);

  // Create form
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [filterCat, setFilterCat] = useState(null);
  const [text, setText] = useState('');
  const [voice, setVoice] = useState('台灣中文 (女性-曉雨)');
  const [generating, setGenerating] = useState(false);
  const [genResult, setGenResult] = useState(null);

  // Post to FB
  const [fbMessage, setFbMessage] = useState('');
  const [fbPosting, setFbPosting] = useState(false);
  const [fbResult, setFbResult] = useState(null);

  // Generated videos
  const [generatedVideos, setGeneratedVideos] = useState([]);
  const [genListLoading, setGenListLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [vRes, voicesRes, catRes, fbRes] = await Promise.all([
        videoAPI.getSourceVideos({ limit: 100 }),
        videoAPI.getVoices(),
        videoAPI.getCategories().catch(() => ({ data: [] })),
        facebookAPI.getStatus().catch(() => ({ data: {} })),
      ]);
      const videos = vRes?.data?.data || vRes?.data || [];
      setSourceVideos(Array.isArray(videos) ? videos : []);
      setVoices(Array.isArray(voicesRes?.data) ? voicesRes.data : []);
      const cats = catRes?.data?.data || catRes?.data || [];
      setCategories(Array.isArray(cats) ? cats : []);
      const fb = fbRes?.data || fbRes || {};
      setFbConfigured(!!fb.configured);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const fetchGeneratedVideos = useCallback(async () => {
    setGenListLoading(true);
    try {
      const res = await videoAPI.listGenerated();
      setGeneratedVideos(res.data || []);
    } catch { /* ignore */ }
    setGenListLoading(false);
  }, []);

  const handleDeleteVideo = async (filename) => {
    try {
      await videoAPI.deleteGenerated(filename);
      message.success('已刪除');
      fetchGeneratedVideos();
    } catch { message.error('刪除失敗'); }
  };

  const [genElapsed, setGenElapsed] = useState(0);

  const handleGenerate = async () => {
    if (!selectedVideo) { message.warning('請選擇人物影片'); return; }
    if (!text.trim()) { message.warning('請輸入文字'); return; }
    setGenerating(true);
    setGenResult(null);
    setFbResult(null);
    setGenElapsed(0);
    try {
      const raw = await videoAPI.createVideoAndWait(
        { source_video_id: selectedVideo.id, text: text.trim(), voice },
        (elapsedMs) => setGenElapsed(elapsedMs),
      );
      const res = raw.data || raw;
      setGenResult(res);
      if (res.ok) message.success('影片生成完成!');
      else message.error(res.error || '影片生成失敗');
    } catch (e) {
      const errMsg = e?.message || '請求失敗';
      message.error(errMsg);
      setGenResult({ ok: false, error: errMsg });
    }
    setGenerating(false);
  };

  const handlePostToFB = async () => {
    if (!genResult?.video_url) {
      message.error('影片尚未生成完成，請重新生成');
      return;
    }
    setFbPosting(true);
    setFbResult(null);
    try {
      const raw = await videoAPI.postVideoToFacebook({
        video_url: genResult.video_url,
        message: fbMessage.trim() || `AI 影片 — ${text.substring(0, 100)}`,
      });
      const res = raw.data || raw;
      setFbResult(res);
      if (res.ok) message.success('影片已發布到 Facebook!');
      else message.error(res.error || '發布失敗');
    } catch (e) { message.error('發布失敗'); }
    setFbPosting(false);
  };

  const filteredVideos = filterCat
    ? sourceVideos.filter(v => v.category_id === filterCat)
    : sourceVideos;

  if (loading) {
    return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /><div style={{ marginTop: 16, color: '#999' }}>Loading...</div></div>;
  }

  const tabItems = [
    // ── Tab 1: Create Video ──
    {
      key: 'create',
      label: <span><VideoCameraOutlined /> 製作影片</span>,
      children: (
        <Row gutter={[16, 16]}>
          {/* Left: video selection */}
          <Col xs={24} lg={10}>
            <Card title="選擇人物影片" size="small" style={{ borderRadius: 12 }}
              extra={
                <Select allowClear placeholder="分類" size="small" style={{ width: 100 }}
                  value={filterCat} onChange={setFilterCat}>
                  {categories.map(c => <Select.Option key={c.id} value={c.id}>{c.name}</Select.Option>)}
                </Select>
              }>
              <div style={{ maxHeight: 400, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {filteredVideos.length === 0 && <Empty description="無影片" />}
                {filteredVideos.map(v => (
                  <div
                    key={v.id}
                    onClick={() => setSelectedVideo(v)}
                    style={{
                      width: 100, cursor: 'pointer', borderRadius: 8,
                      border: selectedVideo?.id === v.id ? '3px solid #764ba2' : '2px solid #f0f0f0',
                      overflow: 'hidden', position: 'relative',
                    }}
                  >
                    <img
                      src={videoAPI.getThumbnailUrl(v.id)}
                      alt={v.description || v.original_filename}
                      style={{ width: '100%', height: 75, objectFit: 'cover' }}
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                    <div style={{ padding: '4px 6px', fontSize: 10, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {v.description || v.original_filename}
                    </div>
                    {selectedVideo?.id === v.id && (
                      <div style={{ position: 'absolute', top: 4, right: 4 }}>
                        <CheckCircleOutlined style={{ color: '#764ba2', fontSize: 16, background: '#fff', borderRadius: '50%' }} />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          </Col>

          {/* Right: text + voice + generate */}
          <Col xs={24} lg={14}>
            <Card title="設定內容" size="small" style={{ borderRadius: 12 }}>
              {selectedVideo && (
                <Alert type="info" showIcon icon={<PlayCircleOutlined />}
                  message={`已選: ${selectedVideo.description || selectedVideo.original_filename}`}
                  style={{ marginBottom: 12, borderRadius: 8 }} />
              )}
              <Form layout="vertical">
                <Form.Item label="文字內容 (TTS 將轉為語音)" required>
                  <TextArea rows={4} value={text} onChange={e => setText(e.target.value)}
                    placeholder="輸入要讓人物說的話..." maxLength={5000} showCount style={{ fontSize: 15 }} />
                </Form.Item>
                <Form.Item label={<span><SoundOutlined /> 語音</span>}>
                  <Select value={voice} onChange={setVoice} size="large">
                    {voices.map(v => <Select.Option key={v.value} value={v.label}>{v.label}</Select.Option>)}
                  </Select>
                </Form.Item>
                <Button
                  type="primary" size="large" block
                  icon={generating ? <LoadingOutlined /> : <VideoCameraOutlined />}
                  loading={generating}
                  onClick={handleGenerate}
                  disabled={!selectedVideo || !text.trim()}
                  style={{
                    height: 52, fontSize: 16,
                    background: selectedVideo && text.trim() ? 'linear-gradient(135deg, #667eea, #764ba2)' : undefined,
                    border: 'none',
                  }}
                >
                  {generating
                    ? `AI 影片生成中...${genElapsed > 0 ? ` (${(genElapsed / 1000).toFixed(0)}秒)` : '(約1-3分鐘)'}`
                    : '生成 AI 對嘴影片'}
                </Button>
              </Form>

              {/* Generation result */}
              {genResult && (
                <div style={{ marginTop: 16 }}>
                  {genResult.ok ? (
                    <Card size="small" style={{ borderRadius: 12, background: '#f6ffed', border: '1px solid #b7eb8f' }}>
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <Space>
                          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />
                          <Text strong style={{ color: '#52c41a' }}>影片生成完成!</Text>
                          <Tag>{genResult.latency_ms ? `${(genResult.latency_ms / 1000).toFixed(1)}s` : ''}</Tag>
                        </Space>
                        {genResult.video_url && (
                          <video controls style={{ width: '100%', maxHeight: 300, borderRadius: 8 }}
                            src={genResult.video_url} />
                        )}

                        {/* Post to Facebook */}
                        <Card size="small" style={{ borderRadius: 8, background: '#f0f5ff' }}>
                          <Text strong><FacebookOutlined style={{ color: '#1877F2' }} /> 發布到 Facebook</Text>
                          <Input.TextArea
                            rows={2} style={{ marginTop: 8 }}
                            value={fbMessage} onChange={e => setFbMessage(e.target.value)}
                            placeholder="影片貼文訊息 (選填)"
                          />
                          {fbResult?.ok ? (
                            <Result status="success" title="影片已發布到 Facebook!" style={{ padding: '12px 0' }}
                              extra={fbResult.post_url && <Button type="link" href={fbResult.post_url} target="_blank">查看影片</Button>} />
                          ) : fbResult && !fbResult.ok ? (
                            <Alert type="error" message={fbResult.error} style={{ marginTop: 8, borderRadius: 8 }} />
                          ) : null}
                          <Button
                            type="primary" icon={<SendOutlined />}
                            loading={fbPosting} onClick={handlePostToFB}
                            disabled={!fbConfigured || !genResult?.video_url || fbResult?.ok}
                            style={{ marginTop: 8, background: '#1877F2', border: 'none' }}
                            block
                          >
                            發布影片到 Facebook
                          </Button>
                          {!genResult?.video_url && (
                            <Text type="warning" style={{ fontSize: 12 }}>影片未生成完成，請重新整理頁面後再試</Text>
                          )}
                          {!fbConfigured && <Text type="warning" style={{ fontSize: 12 }}>Facebook 尚未設定</Text>}
                        </Card>
                      </Space>
                    </Card>
                  ) : (
                    <Alert type="error" showIcon message="影片生成失敗" description={genResult.error}
                      style={{ borderRadius: 8 }} />
                  )}
                </div>
              )}
            </Card>
          </Col>
        </Row>
      ),
    },

    // ── Tab 2: Source Videos Gallery ──
    {
      key: 'gallery',
      label: <span><PlayCircleOutlined /> 素材庫</span>,
      children: (
        <>
          <Alert type="info" showIcon message={`共 ${sourceVideos.length} 個人物影片素材`}
            style={{ marginBottom: 16, borderRadius: 8 }} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {sourceVideos.map(v => (
              <Card key={v.id} size="small" hoverable
                style={{ width: 160, borderRadius: 12, overflow: 'hidden' }}
                cover={
                  <img src={videoAPI.getThumbnailUrl(v.id)} alt={v.description}
                    style={{ height: 100, objectFit: 'cover' }}
                    onError={e => { e.target.style.display = 'none'; }} />
                }
              >
                <div style={{ fontSize: 11 }}>
                  <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.description || v.original_filename}</div>
                  <Tag style={{ fontSize: 10, marginTop: 4 }}>{v.category_name}</Tag>
                </div>
              </Card>
            ))}
          </div>
        </>
      ),
    },

    // ── Tab 3: Generated Videos ──
    {
      key: 'generated',
      label: <span><CloudOutlined /> 已生成影片</span>,
      children: (
        <>
          <div style={{ marginBottom: 12 }}>
            <Button icon={<ReloadOutlined />} onClick={fetchGeneratedVideos}>重新整理</Button>
          </div>
          {generatedVideos.length === 0 && !genListLoading ? (
            <Empty description="尚無已生成的影片" />
          ) : (
            <Table
              dataSource={generatedVideos}
              rowKey="filename"
              loading={genListLoading}
              size="middle"
              pagination={{ pageSize: 15 }}
              columns={[
                {
                  title: '預覽', width: 220,
                  render: (_, r) => (
                    <video
                      src={r.url}
                      controls
                      style={{ width: 200, maxHeight: 120, borderRadius: 8, background: '#000' }}
                    />
                  ),
                },
                {
                  title: '檔案名稱', dataIndex: 'filename', ellipsis: true,
                  render: (v, r) => <a href={r.url} target="_blank" rel="noreferrer">{v}</a>,
                },
                {
                  title: '大小', dataIndex: 'size_mb', width: 90, align: 'right',
                  render: v => `${v} MB`,
                },
                {
                  title: '建立時間', dataIndex: 'created_at', width: 170,
                  render: v => v ? new Date(v * 1000).toLocaleString('zh-TW') : '-',
                },
                {
                  title: '操作', width: 80, align: 'center',
                  render: (_, r) => (
                    <Popconfirm title="確定刪除此影片？" onConfirm={() => handleDeleteVideo(r.filename)}>
                      <Button size="small" icon={<DeleteOutlined />} danger />
                    </Popconfirm>
                  ),
                },
              ]}
            />
          )}
        </>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      {/* Header */}
      <Card
        style={{ marginBottom: 16, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none', borderRadius: 12 }}
        bodyStyle={{ padding: '20px 24px' }}
      >
        <Space size={12}>
          <VideoCameraOutlined style={{ fontSize: 36, color: '#fff' }} />
          <div>
            <Title level={3} style={{ color: '#fff', margin: 0, fontSize: 20 }}>VideoAI 影片</Title>
            <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13 }}>
              文字 → TTS 語音 → AI 對嘴影片 → 發布 Facebook
            </Text>
          </div>
        </Space>
      </Card>

      <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: '12px 16px' }}>
        <Tabs defaultActiveKey="create" size="small" items={tabItems}
          onChange={key => { if (key === 'generated') fetchGeneratedVideos(); }} />
      </Card>
    </div>
  );
}
